import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, catchError, finalize, map, Observable, of, Subject, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { User, UserRole } from '../models/user.model';
import { ApiService } from '../services/api.service';
import { environment } from '../../../environments/environment';

// ── API shapes ────────────────────────────────────────────────────────────────

export interface LoginRequest {
  UserEmail: string;
  password: string;
}

interface LoginApiData {
  userId: number;
  userEmail: string;
  fullName: string;
  roleId: number;
  role: string;
  userType: string;
  accountIds: number[];
  modules: string[];
  accessToken: string;
  refreshToken: string;
  expiresOn: string;
  isDefaultPasswordReset: boolean;
}

interface RefreshRequest {
  accessToken: string;
  refreshToken: string;
}

type RefreshApiData = LoginApiData;

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  errors: Array<{ field: string; message: string }> | null;
  correlationId: string | null;
}

export interface LoginResult {
  user: User;
}

export interface AccountItem {
  accountUserMappingId: number;
  userId: number;
  accountId: number;
  accountName: string;
  accountAbbreviation: string;
  accountIsActive: boolean;
  mappingIsActive: boolean;
  createdBy: string | null;
  createdDate: string;
  modifiedBy: string | null;
  modifiedDate: string | null;
}

// ── sessionStorage ────────────────────────────────────────────────────────────
// The full API response data object is stored, minus the two token fields.
// New fields added by the backend are persisted automatically — no code change needed.

const SESSION_KEY = 'session';

type StoredSession = Omit<LoginApiData, 'accessToken' | 'refreshToken'>;

// ── Service ───────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class AuthService {
  private platformId = inject(PLATFORM_ID);
  private api        = inject(ApiService);
  private http       = inject(HttpClient);
  private router     = inject(Router);

  private accessToken:  string | null = null;
  private refreshToken: string | null = null;

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();

  private sessionExpiringSubject = new Subject<number>();
  sessionExpiring$ = this.sessionExpiringSubject.asObservable();

  private sessionEndedSubject = new Subject<'expired' | 'logout'>();
  sessionEnded$ = this.sessionEndedSubject.asObservable();

  private accountChangedSubject = new Subject<number>();
  accountChanged$ = this.accountChangedSubject.asObservable();

  private accountsSubject = new BehaviorSubject<AccountItem[]>([]);
  accounts$ = this.accountsSubject.asObservable();

  private expiresOn: string | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private warningTimer: ReturnType<typeof setTimeout> | null = null;
  private expiryTimer:  ReturnType<typeof setTimeout> | null = null;

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    // Restore the user subject from sessionStorage so components that read
    // currentUser$ have data immediately (e.g. topbar user name).
    // Tokens are gone after a page reload, so isLoggedIn() will still return
    // false and the auth guard will redirect to /login.
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      try {
        const s: StoredSession = JSON.parse(raw);
        this.expiresOn = s.expiresOn;
        this.accessToken  = sessionStorage.getItem('accessToken');
        this.refreshToken = sessionStorage.getItem('refreshToken');
        this.currentUserSubject.next(this.mapLoginDataToUser(s as LoginApiData));

        // Restart session timers if tokens and expiry are still valid
        if (this.accessToken && this.expiresOn && !this.isTokenExpired(this.expiresOn)) {
          this.startSessionTimers(this.expiresOn);
        } else if (this.expiresOn && this.isTokenExpired(this.expiresOn)) {
          this.clearSession();
        }
      } catch {
        sessionStorage.removeItem(SESSION_KEY);
      }
    }
  }

  // ── Login ─────────────────────────────────────────────────────────────────

  login(email: string, password: string): Observable<LoginResult> {
    this.clearSession();

    const payload: LoginRequest = { UserEmail: email, password };

    return this.api.post<ApiResponse<LoginApiData>>(environment.auth.loginPath, payload).pipe(
      map(res => {
        const d = res.data;
        this.persistSession(d);
        this.startSessionTimers(d.expiresOn);
        return { user: this.currentUserSubject.value! };
      })
    );
  }

  // ── Token Refresh ─────────────────────────────────────────────────────────

  refreshSession(): Observable<boolean> {
    if (!this.accessToken || !this.refreshToken) {
      this.logout();
      return of(false);
    }

    const payload: RefreshRequest = {
      accessToken:  this.accessToken,
      refreshToken: this.refreshToken,
    };

    return this.api.post<ApiResponse<RefreshApiData>>(
      environment.auth.refreshPath,
      payload
    ).pipe(
      map(res => {
        const d = res.data;
        this.persistSession(d);
        this.startSessionTimers(d.expiresOn);
        return true;
      }),
      catchError(() => {
        this.logout();
        return of(false);
      })
    );
  }

  // ── Change Password ───────────────────────────────────────────────────────

  changePassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Observable<ApiResponse<boolean | string>> {
    return this.api.post<ApiResponse<boolean | string>>(
      environment.auth.changePasswordPath,
      { currentPassword, newPassword, confirmPassword }
    ).pipe(
      tap(res => {
        if (res.success) {
          // Clear the mustChangePassword flag in memory and sessionStorage
          const user = this.currentUserSubject.value;
          if (user) {
            this.currentUserSubject.next({ ...user, mustChangePassword: false });
          }
          if (isPlatformBrowser(this.platformId)) {
            const raw = sessionStorage.getItem(SESSION_KEY);
            if (raw) {
              const stored: StoredSession = { ...JSON.parse(raw), isDefaultPasswordReset: true };
              sessionStorage.setItem(SESSION_KEY, JSON.stringify(stored));
            }
          }
        }
      })
    );
  }

  // ── Forgot Password ───────────────────────────────────────────────────────

  forgotPassword(email: string): Observable<ApiResponse<boolean | string>> {
    return this.api.post<ApiResponse<boolean | string>>(
      environment.auth.forgotPasswordPath,
      { UserEmail: email }
    );
  }

  // ── Reset Password ────────────────────────────────────────────────────────

  resetPassword(
    token: string,
    newPassword: string,
    confirmPassword: string
  ): Observable<ApiResponse<boolean | string>> {
    return this.api.post<ApiResponse<boolean | string>>(
      environment.auth.resetPasswordPath,
      { token, newPassword, confirmPassword }
    );
  }

  // ── Sign Out (API + local cleanup) ───────────────────────────────────────
  // Call this when the user explicitly clicks "Sign Out".
  // Sends the logout request to invalidate the server-side token, then clears
  // the local session regardless of the API response (200 or 401).

  signOut(): void {
    const accessToken  = this.accessToken;
    const refreshToken = this.refreshToken;

    // Clear local session immediately so the UI reflects logged-out state
    this.logout();

    // Fire-and-forget: invalidate the token server-side.
    // Must use HttpClient directly with the captured token because clearSession()
    // above already nulled this.accessToken — the JWT interceptor would attach no header.
    if (accessToken && refreshToken) {
      this.http.post<ApiResponse<boolean | string>>(
        `${environment.apiUrl}${environment.auth.logoutPath}`,
        { accessToken, refreshToken },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      ).pipe(
        catchError(() => of(null)),
        finalize(() => {})
      ).subscribe();
    }
  }

  // ── Logout (local cleanup only) ───────────────────────────────────────────
  // Used internally and by the login page to clear stale sessions without
  // calling the API (e.g. page reload, session expiry, auth guard redirect).

  logout(redirectToLogin = true): void {
    this.sessionEndedSubject.next('logout');
    this.clearSession();
    if (redirectToLogin) {
      this.router.navigate(['/login']);
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  isLoggedIn(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    if (!this.accessToken || !this.currentUserSubject.value) return false;
    if (this.expiresOn && this.isTokenExpired(this.expiresOn)) {
      this.clearSession();
      return false;
    }
    return true;
  }

  mustChangePassword(): boolean {
    return !!this.currentUserSubject.value?.mustChangePassword;
  }

  getToken(): string | null {
    return this.accessToken;
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  getTokenExpiry(): Date | null {
    return this.expiresOn ? new Date(this.expiresOn) : null;
  }

  setAccountId(id: number): void {
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem('accountId', String(id));
    }
    this.accountChangedSubject.next(id);
  }

  getAccountId(): number {
    if (isPlatformBrowser(this.platformId)) {
      return Number(sessionStorage.getItem('accountId')) || 0;
    }
    return 0;
  }

  getAccountName(): string {
    const id = this.getAccountId();
    return this.accountsSubject.getValue().find(a => a.accountId === id)?.accountName ?? '';
  }

  getUserAccounts(userId: number): Observable<ApiResponse<AccountItem[]>> {
    if (this.accountsSubject.getValue().length > 0) {
      return of({ success: true, message: '', data: this.accountsSubject.getValue(), errors: null, correlationId: null });
    }
    return this.api.get<ApiResponse<AccountItem[]>>(`${environment.users.userAccountsPath}/${userId}/accounts`).pipe(
      tap(res => this.accountsSubject.next(res.data ?? []))
    );
  }

  // ── Private ───────────────────────────────────────────────────────────────

  canAccessModule(key: string): boolean {
    const modules = this.currentUserSubject.value?.modules ?? ['*'];
    return modules.includes('*') || modules.some(m => m.toLowerCase() === key.toLowerCase());
  }

  getUserType(): string | null {
    return this.currentUserSubject.value?.userType ?? null;
  }

  private mapLoginDataToUser(d: LoginApiData): User {
    const [firstName, ...rest] = d.fullName.trim().split(' ');
    return {
      id:                 d.userId,
      username:           d.userEmail.split('@')[0],
      email:              d.userEmail,
      fullName:           d.fullName,
      firstName:          firstName ?? '',
      lastName:           rest.join(' '),
      role:               d.role as UserRole,
      status:             'active',
      createdAt:          new Date().toISOString(),
      mustChangePassword: !d.isDefaultPasswordReset,
      userType:           d.userType,
      modules:            d.modules ?? ['*'],
      accountIds:         d.accountIds ?? [],
    };
  }

  private persistSession(d: LoginApiData): void {
    this.accessToken  = d.accessToken;
    this.refreshToken = d.refreshToken;
    this.expiresOn    = d.expiresOn;
    this.currentUserSubject.next(this.mapLoginDataToUser(d));

    if (isPlatformBrowser(this.platformId)) {
      const { accessToken, refreshToken, ...sessionData } = d;
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
      sessionStorage.setItem('accessToken', accessToken);
      sessionStorage.setItem('refreshToken', refreshToken);

      // Set first accountId as default if none stored yet
      if (d.accountIds?.length && !sessionStorage.getItem('accountId')) {
        sessionStorage.setItem('accountId', String(d.accountIds[0]));
      }
    }
  }

  private startSessionTimers(expiresOn: string): void {
    this.clearTimers();

    const expiryMs      = new Date(expiresOn).getTime();
    const nowMs         = Date.now();
    const msUntilExpiry = expiryMs - nowMs;

    if (msUntilExpiry <= 0) {
      this.logout();
      return;
    }

    // Proactive refresh 2 min before expiry
    const msUntilRefresh = msUntilExpiry - environment.session.refreshBeforeExpiryMs;
    if (msUntilRefresh > 0) {
      this.refreshTimer = setTimeout(() => {
        this.refreshSession().subscribe();
      }, msUntilRefresh);
    }

    // Warning 30 s before logout
    const msUntilWarning = msUntilExpiry - environment.session.warningBeforeExpiryMs;
    if (msUntilWarning > 0) {
      this.warningTimer = setTimeout(() => {
        const secondsLeft = Math.round(
          (new Date(expiresOn).getTime() - Date.now()) / 1000
        );
        // If the timer fired late — e.g. the machine slept or the tab was
        // throttled — the session may already be at/past expiry. Skip the
        // warning (the expiry timer below signs the user out) so we never show
        // a nonsensical "expires in -N seconds" alongside the expired message.
        if (secondsLeft > 0) {
          this.sessionExpiringSubject.next(secondsLeft);
        }
      }, msUntilWarning);
    }

    // At expiry — try one last refresh before logging out
    this.expiryTimer = setTimeout(() => {
      this.refreshSession().subscribe(success => {
        if (!success) {
          this.sessionEndedSubject.next('expired');
          this.clearSession();
          this.router.navigate(['/login']);
        }
      });
    }, msUntilExpiry);
  }

  private isTokenExpired(expiresOn: string): boolean {
    return new Date(expiresOn).getTime() <= Date.now();
  }

  private clearTimers(): void {
    if (this.refreshTimer) { clearTimeout(this.refreshTimer); this.refreshTimer = null; }
    if (this.warningTimer) { clearTimeout(this.warningTimer); this.warningTimer = null; }
    if (this.expiryTimer)  { clearTimeout(this.expiryTimer);  this.expiryTimer  = null; }
  }

  private clearSession(): void {
    this.clearTimers();
    this.accessToken  = null;
    this.refreshToken = null;
    this.expiresOn    = null;
    this.currentUserSubject.next(null);
    this.accountsSubject.next([]);
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem('accountId');
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('refreshToken');
    }
  }
}
